import React from 'react';
import { useTranslation } from 'react-i18next';
import { Globe2, ChevronDown } from 'lucide-react';
import './LanguageSwitcher.css';

const SUPPORTED_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'it', name: 'Italiano' },
  { code: 'tr', name: 'Türkçe' },
  { code: 'ar', name: 'العربية' },
  { code: 'bn', name: 'বাংলা' }
];

import { useNavigate, useLocation } from 'react-router-dom';

export default function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newLang = e.target.value;
    
    // Explicitly update the cookie FIRST before any navigation logic
    document.cookie = `i18next=${newLang}; path=/; max-age=31536000`;

    // If we are in the dashboard, don't change the URL, just change language in place
    if (location.pathname.startsWith('/dashboard') || location.pathname.startsWith('/onboarding')) {
      i18n.changeLanguage(newLang);
      return;
    }

    // For public pages, update the URL prefix
    const pathParts = location.pathname.split('/').filter(Boolean);
    
    // Check if the first part of the URL is a supported language (excluding English which has no prefix)
    const nonEnglishLangs = SUPPORTED_LANGUAGES.map(l => l.code).filter(c => c !== 'en');
    if (pathParts.length > 0 && nonEnglishLangs.includes(pathParts[0])) {
      pathParts.shift(); // Remove the old language prefix
    }

    const remainingPath = pathParts.join('/');
    const newPrefix = newLang === 'en' ? '' : `/${newLang}`;
    
    // Navigate to the new URL without calling i18n.changeLanguage in-place.
    // The path change will trigger the LanguageRouteWrapper / EnglishRouteWrapper to apply the language change.
    const destPath = `${newPrefix}/${remainingPath}${location.search}${location.hash}`;
    navigate(destPath || '/');
  };

  return (
    <div className="language-switcher">
      <Globe2 className="language-icon" size={18} />
      <div className="language-select-wrapper">
        <select
          value={i18n.language || 'en'}
          onChange={handleLanguageChange}
          className="language-select"
          aria-label={t('langSwitcher.selectLanguage')}
        >
          {SUPPORTED_LANGUAGES.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.name}
            </option>
          ))}
        </select>
        <ChevronDown className="language-chevron" size={14} />
      </div>
    </div>
  );
}
